// ─────────────────────────────────────────────
//  Setup ▸ Provider Map — interactive staffing-geography map (admin).
//  Three campus pins, provider home dots (red = doctors, green = CRNAs,
//  teal = other AHPs), and toggleable drive-time rings per facility with an
//  admin-editable minute value (default 30). Leaflet + OpenStreetMap tiles —
//  no API key.
//
//  Addresses come from BOTH live sources, merged per person:
//    • the staff record (address/geo — editable below the map), and
//    • the person's own PROFILE (users.address) via the uid link or name.
//  Anyone with an address but no geocode is located AUTOMATICALLY while this
//  page is open (Nominatim, 1 req/sec, result cached on the record) — so a
//  provider who saves their address in Profile appears on the map without
//  any admin action beyond having the page open.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { collection, onSnapshot } from "../firebase/sdk";
import { db } from "../firebase/config";
import { useToast } from "../context/ToastContext";
import { listenToStaff, updateStaff, updateUser } from "../firebase/firestore";
import {
  FACILITY_PINS,
  DEFAULT_RADIUS_MINUTES,
  minutesToRadiusMeters,
  providerDotColor,
  DOT_COLORS,
  mappableStaff,
  ungeocodedStaff,
} from "../domain/providerMap";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// Per-device map preferences (radius minutes + which rings are on).
const PREFS_KEY = "atc-provider-map";
const loadPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
};

const norm = (v) => String(v || "").trim().toLowerCase().replace(/^dr\.?\s+/, "");
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Accept only results that plausibly ARE Tampa Bay commuting country — a bare
// street line can otherwise geocode to the wrong state entirely.
const inTampaBayArea = (g) =>
  g && Number.isFinite(g.lat) && g.lat > 27 && g.lat < 29.2 && g.lng > -83.4 && g.lng < -81.5;

// Esri World geocoder (keyless, same TomTom data family as the basemap).
// Nominatim's house-number interpolation placed 17228 Breeders Cup Dr ~500 m
// off; Esri matches the Google-verified rooftop. Bare street lines get a
// ", FL" hint; only high-score hits inside the Tampa Bay box count.
async function geocode(address) {
  const q = /,\s*fl|florida|\b33\d{3}\b/i.test(address) ? address : `${address}, FL`;
  const url = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?f=json&maxLocations=1&singleLine=${encodeURIComponent(q)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Geocoder HTTP ${res.status}`);
  const data = await res.json();
  const c = data.candidates?.[0];
  if (!c || c.score < 80) return null;
  const g = { lat: Number(c.location.y), lng: Number(c.location.x) };
  return inTampaBayArea(g) ? g : null;
}

const facilityIcon = (label) =>
  L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px)">
      <span style="font-size:26px;line-height:1">📍</span>
      <span style="background:#1e40af;color:#fff;font:700 10px system-ui;border-radius:6px;padding:2px 7px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3)">${label}</span>
    </div>`,
    iconSize: [0, 0],
  });

export function ProviderMapTab() {
  const toast = useToast();
  // RAW staff docs (not the normalized board pool — that strips address/geo)…
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    const unsub = listenToStaff(
      (snap) => setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("[ProviderMap] staff listener failed:", err)
    );
    return unsub;
  }, []);
  // …AND every login profile, because people type their home address there.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("[ProviderMap] users listener failed:", err)
    );
    return unsub;
  }, []);

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({ circles: {}, dots: null });

  const prefs = useRef(loadPrefs());
  const [radiusMinutes, setRadiusMinutes] = useState(
    prefs.current.radiusMinutes || DEFAULT_RADIUS_MINUTES
  );
  const [ringsOn, setRingsOn] = useState(
    prefs.current.ringsOn || { default: true, tampa: true, carrollwood: true }
  );
  const [geocoding, setGeocoding] = useState(false);
  const [addrDrafts, setAddrDrafts] = useState({});

  // One merged person list: staff record enriched by the linked profile
  // (uid link first, exact name fallback), plus profile-only people who have
  // an address but no staff record yet.
  const people = useMemo(() => {
    const byUid = new Map(users.map((u) => [u.id, u]));
    const byName = new Map(users.map((u) => [norm(u.name), u]));
    const merged = staff.map((m) => {
      const u = (m.uid && byUid.get(m.uid)) || byName.get(norm(m.name)) || null;
      return {
        ...m,
        address: String(m.address || "").trim() || String(u?.address || "").trim(),
        geo: m.geo || u?.geo || null,
        _writeTo: { staffId: m.id },
      };
    });
    for (const u of users) {
      if (!String(u.address || "").trim()) continue;
      // Administrators are not clinical providers — never plotted.
      if (String(u.role || "").toLowerCase() === "admin") continue;
      if (staff.some((m) => m.uid === u.id || norm(m.name) === norm(u.name))) continue;
      merged.push({
        id: `user-${u.id}`,
        name: u.name,
        role: u.role,
        subRole: u.subRole || null,
        address: String(u.address).trim(),
        geo: u.geo || null,
        _writeTo: { userId: u.id },
      });
    }
    return merged;
  }, [staff, users]);

  const dots = useMemo(() => mappableStaff(people), [people]);
  const pending = useMemo(() => ungeocodedStaff(people), [people]);
  const noAddress = useMemo(
    () => people.filter((m) => !String(m.address || "").trim() && !m.geo).length,
    [people]
  );

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ radiusMinutes, ringsOn }));
  }, [radiusMinutes, ringsOn]);

  // Map init — once.
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: true }).setView([28.105, -82.43], 10);
    // Esri World Street Map (TomTom/Garmin commercial street data) — OSM's
    // community labels were wrong/garbled in newer subdivisions (Breeders Cup
    // Dr, Oakley Scott Ln). Esri matches Google-grade naming, needs no API
    // key, and drops in as a plain tile URL. Geocoding stays on Nominatim —
    // coordinates were always correct, only the drawn labels were off.
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        attribution: "Tiles &copy; Esri — Sources: Esri, TomTom, Garmin, FAO, NOAA, USGS, &copy; OpenStreetMap contributors",
      }
    ).addTo(map);
    for (const f of FACILITY_PINS) {
      L.marker([f.lat, f.lng], { icon: facilityIcon(f.label.replace("AdventHealth ", "")), zIndexOffset: 1000 })
        .addTo(map)
        .bindPopup(`<strong>${f.label}</strong><br/>${f.address}`);
    }
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Drive-time rings — follow the toggles and the minute value.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const radius = minutesToRadiusMeters(radiusMinutes);
    for (const f of FACILITY_PINS) {
      const existing = layersRef.current.circles[f.id];
      if (existing) {
        map.removeLayer(existing);
        delete layersRef.current.circles[f.id];
      }
      if (ringsOn[f.id] && radius > 0) {
        layersRef.current.circles[f.id] = L.circle([f.lat, f.lng], {
          radius,
          color: "#1e40af",
          weight: 1.5,
          fillColor: "#1e40af",
          fillOpacity: 0.06,
          dashArray: "6 6",
        }).addTo(map);
      }
    }
  }, [ringsOn, radiusMinutes]);

  // Provider dots — rebuild when the merged geo data changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layersRef.current.dots) map.removeLayer(layersRef.current.dots);
    const group = L.layerGroup();
    for (const m of dots) {
      L.circleMarker([Number(m.geo.lat), Number(m.geo.lng)], {
        radius: 7,
        color: "#ffffff",
        weight: 2,
        fillColor: providerDotColor(m),
        fillOpacity: 0.95,
      })
        .bindPopup(
          `<strong>${m.name || "Staff"}</strong><br/>${(m.role || "").toUpperCase()}${m.address ? `<br/>${m.address}` : ""}`
        )
        .addTo(group);
    }
    group.addTo(map);
    layersRef.current.dots = group;
  }, [dots]);

  // Persist a geocode onto the person's own record (staff doc when they have
  // one, else their login profile).
  const writeGeo = (m, patch) =>
    m._writeTo?.staffId ? updateStaff(m._writeTo.staffId, patch) : updateUser(m._writeTo.userId, patch);

  const runGeocode = async (list) => {
    setGeocoding(true);
    let ok = 0, fail = 0;
    try {
      for (const m of list) {
        try {
          const geo = await geocode(m.address);
          if (geo) {
            await writeGeo(m, { geo });
            ok++;
          } else fail++;
        } catch (e) {
          console.error(`Geocode failed for ${m.name}:`, e);
          fail++;
        }
        await sleep(1100); // Nominatim rate limit
      }
      if (ok || fail) {
        toast[fail ? "info" : "success"](
          `Located ${ok} provider${ok === 1 ? "" : "s"} on the map${fail ? ` — ${fail} address${fail === 1 ? "" : "es"} need a city/ZIP to resolve` : ""}.`
        );
      }
    } finally {
      setGeocoding(false);
    }
  };

  // AUTO-locate: any person with an address but no geocode is looked up as
  // soon as this page sees them — including a provider who saves their
  // address in Profile while the map is open. Each id is attempted once per
  // visit; editing the address re-queues it.
  const attempted = useRef(new Set());
  useEffect(() => {
    if (geocoding) return;
    const todo = pending.filter((m) => !attempted.current.has(m.id));
    if (!todo.length) return;
    todo.forEach((m) => attempted.current.add(m.id));
    runGeocode(todo);
  }, [pending, geocoding]);

  const saveAddress = async (m) => {
    const address = (addrDrafts[m.id] ?? "").trim();
    try {
      // New address invalidates any previous geocode; re-queue the lookup.
      await writeGeo(m, { address, geo: null });
      attempted.current.delete(m.id);
      setAddrDrafts((d) => ({ ...d, [m.id]: undefined }));
      toast.success(`${m.name}: address saved — locating on the map…`);
    } catch (e) {
      console.error("Save address failed:", e);
      toast.error("Could not save the address.");
    }
  };

  const legendDot = (fill) => ({
    display: "inline-block", width: 12, height: 12, borderRadius: "50%",
    background: fill, border: "2px solid #fff", boxShadow: "0 0 0 1px #0002", marginRight: 6, verticalAlign: -1,
  });

  return (
    <div>
      <div style={s.tabHeader}>
        <div>
          <div style={s.tabTitle}>Provider Map</div>
          <div style={s.tabSub}>
            Where the team lives relative to each campus. Dots come from staff
            records AND each person's profile address; rings approximate drive
            time (straight-line at ~45 km/h).
          </div>
        </div>
      </div>

      <div style={s.panel}>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, display: "flex", alignItems: "center", gap: 8 }}>
            Drive-time ring
            <input
              type="number"
              min="1"
              max="240"
              value={radiusMinutes}
              onChange={(e) => setRadiusMinutes(Math.max(1, Math.min(240, Number(e.target.value) || DEFAULT_RADIUS_MINUTES)))}
              style={{ ...s.select, width: 74, padding: "6px 8px" }}
              aria-label="Drive-time ring in minutes"
            />
            minutes
          </label>
          {FACILITY_PINS.map((f) => (
            <label key={f.id} style={{ fontSize: 12, fontWeight: 600, color: color.text.secondary, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={!!ringsOn[f.id]}
                onChange={(e) => setRingsOn((r) => ({ ...r, [f.id]: e.target.checked }))}
              />
              {f.label.replace("AdventHealth ", "")} ring
            </label>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 12, color: color.text.secondary }}>
            <span style={legendDot(DOT_COLORS.doctor)} />Doctors
            <span style={{ ...legendDot(DOT_COLORS.crna), marginLeft: 14 }} />CRNAs
            <span style={{ ...legendDot(DOT_COLORS.other), marginLeft: 14 }} />Other AHPs
          </span>
        </div>

        <div
          ref={mapEl}
          style={{ height: 520, borderRadius: 10, border: `1px solid ${color.border.default}`, overflow: "hidden" }}
          aria-label="Provider location map"
        />
        <p style={{ fontSize: 11, color: color.text.muted, margin: "8px 0 0" }} aria-live="polite">
          {dots.length} provider{dots.length === 1 ? "" : "s"} on the map
          {geocoding ? " · locating new addresses…" : pending.length ? ` · ${pending.length} address${pending.length === 1 ? "" : "es"} queued` : ""} ·{" "}
          {noAddress} with no address on file.
        </p>
      </div>

      <div style={s.panel}>
        <div style={s.panelTitle}>📮 Provider addresses</div>
        <p style={{ fontSize: 12, color: color.text.secondary, margin: "6px 0 12px" }}>
          Pulled from staff records and login profiles. Add or fix one below —
          it's located on the map automatically. If an address won't resolve,
          add its city and ZIP.
        </p>
        <div style={{ marginTop: 6, maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {people
            .slice()
            .sort((a, b) => {
              // People with any address first, then alphabetical.
              const aa = a.address ? 0 : 1;
              const bb = b.address ? 0 : 1;
              if (aa !== bb) return aa - bb;
              return (a.name || "").localeCompare(b.name || "");
            })
            .map((m) => (
              <div key={m.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={legendDot(providerDotColor(m))} />
                <span style={{ width: 190, fontSize: 12, fontWeight: 700, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.name}
                </span>
                <input
                  style={{ ...s.select, flex: 1, padding: "6px 9px", fontSize: 12 }}
                  placeholder="Street address, city, state, ZIP"
                  value={addrDrafts[m.id] ?? m.address ?? ""}
                  onChange={(e) => setAddrDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                  aria-label={`Home address for ${m.name}`}
                />
                {(addrDrafts[m.id] ?? m.address ?? "") !== (m.address || "") && (
                  <button style={{ ...s.outlineBtn, padding: "5px 12px" }} onClick={() => saveAddress(m)}>Save</button>
                )}
                <span style={{ width: 64, fontSize: 10, fontWeight: 700, color: m.geo ? "#0a7d5e" : color.text.muted, textAlign: "right" }}>
                  {m.geo ? "on map" : m.address ? (geocoding ? "locating…" : "queued") : "no address"}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
